import * as THREE from 'three';

// Loads public/textures/flapfont.<hash>.{webp,png} + flapfont.json once, at
// application startup, and hands the resolved texture + manifest to whoever
// asks. Built offline by `npm run build:assets`
// (tools/build-flapfont.mjs) - nothing here rasterises a glyph.
//
// Same preload/get shape as crowd-atlas.js / paper-texture.js, and the same
// RELATIVE urls for the same reason: express.static sends no
// Access-Control-Allow-Origin, so an absolute cross-origin fetch is blocked
// outright when this page runs from the Tizen app's own origin. A relative
// path resolves against the Pi when it serves party-mode.html and against the
// app's own directory when the TV bundle carries its own copy.
//
// A missing atlas is a DEGRADED visualizer, never a broken page: splitflap.js
// renders its panel, its flaps and its whole flip mechanism with no glyphs at
// all (see its own _resolveFont).
const MANIFEST_URL = 'textures/flapfont.json';

let _font = null;
let _promise = null;

function configure(texture) {
  // The atlas is flat white with the shape in alpha; the shader reads only
  // .a, so the transfer curve never enters into it. Marked sRGB anyway for
  // consistency with every other texture here - it changes nothing that is
  // actually sampled.
  texture.colorSpace = THREE.SRGBColorSpace;
  // Stored straight (flat white under transparent texels too), so
  // premultiplying here would be wrong twice over.
  texture.premultiplyAlpha = false;
  // A 24-column board puts each glyph at roughly 1:1 against its 88px atlas
  // box at 1080p, and BELOW that on a lower tier or a smaller window - so it
  // is minified, and without a mip chain the stems alias into crawling noise.
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  // Glyph cells sit next to each other in the atlas: clamping is what stops a
  // sample at a cell edge from wrapping to the far side of the sheet. The
  // half-texel inset baked into the exported UVs does the rest.
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function loadTexture(url) {
  return new Promise(function (resolve, reject) {
    new THREE.TextureLoader().load(url, function (t) { resolve(configure(t)); }, undefined, function () {
      reject(new Error(url + ' failed to load'));
    });
  });
}

// Resolves to { texture, meta, charIndex } or to null. Never rejects.
//
// charIndex is built here rather than in every consumer: the manifest carries
// the charset as a string, and what a visualizer actually needs is
// character -> atlas slot.
export function preloadFlapFont() {
  if (_promise) return _promise;
  _promise = fetch(MANIFEST_URL)
    .then(function (r) {
      if (!r.ok) throw new Error('flapfont.json HTTP ' + r.status);
      return r.json();
    })
    .then(function (meta) {
      return loadTexture('textures/' + meta.webp)
        .catch(function () { return loadTexture('textures/' + meta.png); })
        .then(function (texture) {
          const charIndex = new Map();
          const charset = meta.charset || '';
          for (let i = 0; i < charset.length; i++) {
            // First occurrence wins - the charset has no duplicates, and if a
            // future one did, the earlier slot is the one the manifest's own
            // glyph list describes.
            if (!charIndex.has(charset[i])) charIndex.set(charset[i], i);
          }
          _font = { texture: texture, meta: meta, charIndex: charIndex };
          if (meta.fontFallback) {
            console.warn('[party-mode] flap atlas was built with the default sans:'
              + ' no condensed face was available to tools/build-flapfont.mjs');
          }
          return _font;
        });
    })
    .catch(function (err) {
      console.error('[party-mode] flap atlas unavailable, Splitflap will render blank flaps', err);
      _font = null;
      return null;
    });
  return _promise;
}

// Synchronous accessor for construction sites that cannot await (the
// switcher's factories, adaptive-party's lazy mode swap). Null until the
// preload above has resolved.
export function getFlapFont() {
  return _font;
}

// The atlas is shared and preloaded: whoever uses it must NOT dispose it.
// Exposed only so the leak test can account for the one texture it adds.
export function isFlapFontLoaded() {
  return _font !== null;
}
